import type { PicklistDto, PicklistItemGroupDto, PicklistPlannedLineDto, ReleasedQuantityDto } from '../../types';
import { PostingStatusEnum, ReceivingItemType } from '../../types';
import { mrsHeadroom } from '../../utils/mrsHeadroom';
import { qty, unitWord } from '../../utils/picklistProgress';

/**
 * PT-5 — the Post to MIS dialog's pure layer: rows, the MIS group key, headroom and over-MRS, the default
 * selection, the mixed-selection banner, and every computed string (the O-20 settlement,
 * `o20/PT-5_full_file_settlement.md`). No React, no fetch: vitest pins it (`postRows.test.ts`).
 *
 * A row is one PLANNED LINE (`itemGroups[].plannedLines[]`), which is one `PicklistItem` — the `PicklistItemId`
 * the request posts. Rows never come from `items[]`, which is read only for the reading seed.
 */

/** Mirrors the server's `PostPicklistRequestDtoValidator.MemoMaxLength` (a C# constant cannot reach the web). */
export const MEMO_MAX_LENGTH = 980; // ISS-R-108: NetSuite takes 999 per line memo, the picklist number included

/** One picked serial slot of a row, for the Serials sheet. */
export interface SerialUnit {
  /** The slot (`picklistItemBinId`) a `UnitsToIssue` entry names. */
  slotId: number;
  inventoryNumberId: number;
  serial: string;
  bin: string;
  /** Picked, not posted, and not held by an open batch: tickable. */
  available: boolean;
  /** The prototype's state tag for a unit that is not available. */
  tag: '' | 'Posting' | 'Posted';
}

export interface PostRow {
  /** The planned line's id = the `PicklistItemId` this row posts. */
  id: number;
  /** The item (group) anchor — the reading is one value per item. */
  anchorId: number;
  itemCode: string;
  /** This MRS line's own description (O-22, O-33). */
  itemName: string;
  unit: string;
  isSerial: boolean;
  mrsNumber: string;
  materialRequestId: number;
  materialRequestItemId: number;
  requestorFullName: string;
  accountRequestorId: number;
  departmentId: number;
  /** One value, shown once as the Cost center (O-10). */
  departmentName: string;
  endUseId: number;
  endUseName: string;
  remarks: string;
  picked: number;
  posted: number;
  /** This picklist's open batches holding this line: Pending, Claimed, Failed (PW-01). */
  posting: number;
  /** picked − posted − posting: what the row can post. */
  max: number;
  mrsRequested: number;
  mrsReleased: number;
  /** Every picklist's Pending/Claimed posts on this MRS line (C3; the by-id read). */
  mrsInFlight: number;
  postingBatchIds: number[];
  /** Serial rows: every picked slot of this line, available first. */
  serials: SerialUnit[];
  /** C4 — rows in one MIS share one key. */
  groupKey: string;
  /** "{k} of {n} MRS" when the item has 2+ postable rows, else null (O-20 settlement slot 52). */
  splitLabel: string | null;
  splitTip: string | null;
}

const isRollingMill1 = (department: string) => department.trim().toLowerCase() === 'rolling mill 1';

/**
 * C4 — the MIS group key, mirroring `MisSelectionRules.EnsureConsistent`: one endorser, one department, and —
 * in Rolling Mill 1 only — one end use. These are NetSuite header rules; a mixed selection has no legal MIS.
 */
export function groupKeyOf(line: Pick<PostRow, 'accountRequestorId' | 'departmentId' | 'departmentName' | 'endUseId'>): string {
  return `${line.accountRequestorId}|${line.departmentId}|${isRollingMill1(line.departmentName) ? line.endUseId : '-'}`;
}

/** The prototype's `unitWord` (shared with Adjust; defined beside `qty`). */
export { unitWord };

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

function isSerialGroup(group: PicklistItemGroupDto): boolean {
  return group.itemType === ReceivingItemType.Serialized
    || (group.itemType == null && (group.bins ?? []).some((b) => b.inventoryNumberId != null));
}

/** The picked serial slots of one row, from the group's display bins (a serial display bin is one slot). */
function serialUnitsOf(group: PicklistItemGroupDto, lineId: number): SerialUnit[] {
  const units: SerialUnit[] = [];
  for (const bin of group.bins ?? []) {
    if (bin.inventoryNumberId == null) continue;
    const line = (bin.lines ?? []).find((l) => l.picklistItemId === lineId);
    if (!line || line.currentlyPickedQuantity <= 0) continue;
    const unposted = line.currentlyPickedQuantity - line.postedQuantity > 0;
    const held = (bin.postingQuantity ?? 0) > 0;
    units.push({
      slotId: line.picklistItemBinId,
      inventoryNumberId: bin.inventoryNumberId,
      serial: bin.inventoryNumber ?? `#${bin.inventoryNumberId}`,
      bin: bin.binNumber,
      available: unposted && !held,
      tag: !unposted ? 'Posted' : held ? 'Posting' : '',
    });
  }
  // The prototype's order: available first, then by serial.
  return units.sort((a, b) => Number(b.available) - Number(a.available) || a.serial.localeCompare(b.serial));
}

function toRow(group: PicklistItemGroupDto, line: PicklistPlannedLineDto, isSerial: boolean): PostRow {
  const picked = line.quantityPicked ?? 0;
  const posted = line.quantityPosted ?? 0;
  const posting = line.postingQuantity ?? 0;
  const row: PostRow = {
    id: line.id,
    anchorId: group.id,
    itemCode: group.itemCode,
    itemName: line.itemName || group.itemName,
    unit: group.unitOfMeasure ?? '',
    isSerial,
    mrsNumber: line.mrsNumber,
    materialRequestId: line.materialRequestId,
    materialRequestItemId: line.materialRequestItemId,
    requestorFullName: line.requestorFullName ?? '',
    accountRequestorId: line.accountRequestorId,
    departmentId: line.netsuiteDepartmentId,
    departmentName: line.departmentName ?? '',
    endUseId: line.netsuiteEndUseId,
    endUseName: line.netsuiteEndUseName ?? '',
    remarks: line.remarks ?? '',
    picked,
    posted,
    posting,
    max: Math.max(picked - posted - posting, 0),
    mrsRequested: line.mrsRequestedQuantity ?? 0,
    mrsReleased: line.mrsReleasedQuantity ?? 0,
    mrsInFlight: line.mrsInFlightQuantity ?? 0,
    postingBatchIds: line.postingBatchIds ?? [],
    serials: isSerial ? serialUnitsOf(group, line.id) : [],
    groupKey: '',
    splitLabel: null,
    splitTip: null,
  };
  row.groupKey = groupKeyOf(row);
  return row;
}

/**
 * The dialog's rows: one per planned line with something left to post (`picked − posted − posting > 0`), in
 * group order as served (by anchor), then planned-line order. A fully in-flight line is not a row; it appears
 * only in the already-posting notice.
 */
export function buildPostRows(picklist: PicklistDto): PostRow[] {
  const rows: PostRow[] = [];
  for (const group of picklist.itemGroups ?? []) {
    const isSerial = isSerialGroup(group);
    const itemRows = (group.plannedLines ?? [])
      .map((line) => toRow(group, line, isSerial))
      .filter((row) => row.max > 0);
    // O-20 slot 52: the pill shows whenever the item has 2+ postable rows, same-MRS rows included.
    if (itemRows.length > 1) {
      itemRows.forEach((row, i) => {
        row.splitLabel = `${i + 1} of ${itemRows.length} MRS`;
        row.splitTip =
          `This item was picked for ${itemRows.length} MRS lines. Each becomes its own MIS line, so it is listed once per MRS.`;
      });
    }
    rows.push(...itemRows);
  }
  return rows;
}

/** Planned lines this picklist has handed to an open batch — the already-posting notice (per planned line). */
export interface LockedLine {
  id: number;
  itemCode: string;
  qty: string;
  batch: string;
}

export function lockedLines(picklist: PicklistDto): LockedLine[] {
  const out: LockedLine[] = [];
  for (const group of picklist.itemGroups ?? []) {
    for (const line of group.plannedLines ?? []) {
      const q = line.postingQuantity ?? 0;
      if (q <= 0) continue;
      out.push({
        id: line.id,
        itemCode: group.itemCode,
        qty: `${qty(q)} ${unitWord(group.unitOfMeasure ?? '', q)}`,
        batch: (line.postingBatchIds ?? []).map((b) => `batch #${b}`).join(', '),
      });
    }
  }
  return out;
}

export const lockedNote = (n: number): string =>
  `${n} ${plural(n, 'line is', 'lines are')} already posting to NetSuite and excluded here — that is what stops the same units being issued twice.`;

export const lockedButton = (n: number, open: boolean): string =>
  open ? 'Hide lines' : `View ${n} ${plural(n, 'line', 'lines')}`;

/**
 * The reading seed, ONE per item (C6): the newest non-null reading among the picking histories of the item's
 * lines (by `pickedAt`, then id). Restore returns to it.
 */
export function readingSeeds(picklist: PicklistDto): Record<number, number | null> {
  const byId = new Map((picklist.items ?? []).map((i) => [i.id, i] as const));
  const seeds: Record<number, number | null> = {};
  for (const group of picklist.itemGroups ?? []) {
    let best: { at: string; id: number; reading: number } | null = null;
    for (const line of group.plannedLines ?? []) {
      for (const h of byId.get(line.id)?.pickingHistories ?? []) {
        if (h.reading == null) continue;
        if (!best || h.pickedAt > best.at || (h.pickedAt === best.at && h.id > best.id)) {
          best = { at: h.pickedAt, id: h.id, reading: h.reading };
        }
      }
    }
    seeds[group.id] = best?.reading ?? null;
  }
  return seeds;
}

// ── Headroom and over-MRS (C3, C6) ─────────────────────────────────────────────────────────────────────────

/**
 * PW-24 / PW-06 — the live lookup is unusable when it returned no figure, or a fabricated (0, 0) while the cached
 * demand is above 0. Then the cached figures stand in (O-9 = A: a failed lookup never blocks, it degrades).
 */
export function liveUsable(row: Pick<PostRow, 'mrsRequested'>, live: ReleasedQuantityDto | undefined): boolean {
  if (live?.releasedQuantity == null || live.requestedQuantity == null) return false;
  return !(live.releasedQuantity === 0 && live.requestedQuantity === 0 && row.mrsRequested > 0);
}

export interface RowFigures {
  requested: number;
  released: number;
  fromLive: boolean;
  /** H — what the MRS line can still take from this post; null when no demand is recorded (no check). */
  headroom: number | null;
}

/** C3 — `H = max(0, requested − released − mrsInFlight)`, live figures when usable, else cached. */
export function rowFigures(row: PostRow, live: ReleasedQuantityDto | undefined): RowFigures {
  const fromLive = liveUsable(row, live);
  const requested = fromLive ? live!.requestedQuantity! : row.mrsRequested;
  const released = fromLive ? live!.releasedQuantity! : row.mrsReleased;
  const headroom = requested > 0
    ? mrsHeadroom(
        { mrsRequestedQuantity: requested, mrsReleasedQuantity: released, quantityPicked: row.picked, quantityPosted: row.posted },
        { posting: row.posting, mrsInFlight: row.mrsInFlight },
      ).headroom
    : null;
  return { requested, released, fromLive, headroom };
}

/**
 * ISS-R-117 — the To post a quantity row opens at: its maximum, capped at what its MRS still allows (owner
 * 2026-10-06). The page counts an over line up to its headroom, so the dialog opens at the same figure instead of
 * at the full quantity marked over. Read from the cached figures, because the dialog opens before the live ones
 * arrive; if those differ, the row shows the difference as it always has.
 *
 * Serial rows are not capped: which serial to leave out is the poster's choice, so every serial stays ticked.
 */
export function openingQuantity(row: PostRow): number {
  if (row.isSerial) return row.max;
  const headroom = rowFigures(row, undefined).headroom;
  return headroom == null ? row.max : Math.min(row.max, headroom);
}

/** X — how far Q overshoots H; 0 when not over, or when no demand is recorded. */
export const overBy = (q: number, figures: RowFigures): number =>
  figures.headroom == null || !Number.isFinite(q) ? 0 : Math.max(q - figures.headroom, 0);

/** A To post value that is blank, not a number, negative or above the row's max (live validation, slot 45). */
export const qtyError = (q: number, max: number): boolean => !Number.isFinite(q) || q < 0 || q > max;

/** A reading that is typed but not a non-negative number (slot 39). Blank is fine: it sends none. */
export function readingError(text: string): boolean {
  if (text.trim() === '') return false;
  const n = Number(text);
  return !Number.isFinite(n) || n < 0;
}

/** The To post tooltip (slots 31, 32): over, error, or the normal sentence — every control has one. */
export function qtyTip(row: PostRow, q: number, figures: RowFigures, availableSerials: number): string {
  const x = overBy(q, figures);
  if (x > 0) {
    return `Over MRS by ${qty(x)} — ${row.mrsNumber} can take ${qty(figures.headroom!)} more `
      + `${unitWord(row.unit, figures.headroom!)}, but ${qty(q)} ${unitWord(row.unit, q)} `
      + `${row.isSerial ? 'are selected' : 'are set to post'}, so this line can’t be posted.`;
  }
  if (!row.isSerial && qtyError(q, row.max)) {
    return `Enter 0 to ${qty(row.max)} ${unitWord(row.unit, row.max)} — that is what is picked for ${row.mrsNumber} and not yet posted.`;
  }
  return row.isSerial
    ? `Choose which of the ${availableSerials} picked serials to post.`
    : `Up to ${qty(row.max)} ${unitWord(row.unit, row.max)} picked for ${row.mrsNumber} and not yet posted.`;
}

/** The sub-line "{released} of {requested} issued" and its tone (slot 36, with O-9's cached fallback). */
export function mrsLine(figures: RowFigures, lookupFailed: boolean): { text: string; tone: 'muted' | 'warning' } {
  return {
    text: `${qty(figures.released)} of ${qty(figures.requested)} issued`,
    tone: lookupFailed && !figures.fromLive ? 'warning' : 'muted',
  };
}

// ── Selection, groups, the mixed banner (deliverable 3, C4) ─────────────────────────────────────────────────

/**
 * Deliverable 3: every postable row selected; when the rows span 2+ MIS keys, only the first key's rows, and the
 * dialog opens grouped by endorser. (The prototype leaves over rows unticked; the ticket's explicit rule wins.)
 */
export function defaultSelection(rows: readonly PostRow[]): { selected: number[]; groupBy: 'none' | 'endorser' } {
  const keys = [...new Set(rows.map((r) => r.groupKey))];
  if (keys.length <= 1) return { selected: rows.map((r) => r.id), groupBy: 'none' };
  return { selected: rows.filter((r) => r.groupKey === keys[0]).map((r) => r.id), groupBy: 'endorser' };
}

/** The requestor note, when the picklist's rows have 2+ requestors (over rows counted too). */
export function requestorNote(rows: readonly PostRow[]): string | null {
  const n = new Set(rows.map((r) => r.accountRequestorId)).size;
  return n > 1 ? `${n} requestors on this picklist — each gets their own MIS, so post one group at a time.` : null;
}

export interface MixedState {
  mixed: boolean;
  message: string | null;
}

/** C4 — two or more keys among the selected rows: the banner's sentence, or none. */
export function mixedState(selected: readonly PostRow[]): MixedState {
  const keys = new Set(selected.map((r) => r.groupKey));
  if (keys.size <= 1) return { mixed: false, message: null };
  const requestors = new Set(selected.map((r) => r.accountRequestorId)).size;
  if (requestors > 1) {
    return {
      mixed: true,
      message: `One MIS goes to one requestor, and the selected lines are for ${requestors}. Post one requestor now — the other lines stay picked for the next batch.`,
    };
  }
  // O-32: one requestor, several keys — the server's own sentence, byte for byte.
  const departments = new Set(selected.map((r) => r.departmentId)).size;
  return {
    mixed: true,
    message: departments > 1
      ? 'Cannot post MIS: all items must be from the same department.'
      : 'Cannot post MIS: all items for Rolling Mill 1 must have the same end use.',
  };
}

export interface PostOnlyOption {
  key: string;
  name: string;
  /** "{n} line(s)" — the rows the press will select. */
  count: string;
  rowIds: number[];
}

/**
 * C4 / slot 27 — one "Post only" per key in the selection, first appearance. The press selects ALL of that key's
 * rows that are not over at their full quantity (the prototype's `onlyEndorser`), so the count says that number.
 */
export function postOnlyOptions(
  rows: readonly PostRow[],
  selected: readonly PostRow[],
  isOverAtMax: (row: PostRow) => boolean,
): PostOnlyOption[] {
  const keys = [...new Set(selected.map((r) => r.groupKey))];
  if (keys.length <= 1) return [];
  const keysByRequestor = new Map<number, Set<string>>();
  for (const r of selected) {
    keysByRequestor.set(r.accountRequestorId, (keysByRequestor.get(r.accountRequestorId) ?? new Set()).add(r.groupKey));
  }
  return keys.map((key) => {
    const first = selected.find((r) => r.groupKey === key)!;
    const ownKeys = keysByRequestor.get(first.accountRequestorId)?.size ?? 1;
    const name = ownKeys > 1
      ? [first.requestorFullName, first.departmentName, isRollingMill1(first.departmentName) ? first.endUseName : '']
          .filter(Boolean).join(' · ')
      : first.requestorFullName || '—';
    const rowIds = rows.filter((r) => r.groupKey === key && !isOverAtMax(r)).map((r) => r.id);
    return { key, name, count: `${rowIds.length} ${plural(rowIds.length, 'line', 'lines')}`, rowIds };
  });
}

// ── The details column (slots 34, 51) ─────────────────────────────────────────────────────────────────────

export const overNote = (n: number): string =>
  `${n} ${plural(n, 'selected line exceeds', 'selected lines exceed')} what its MRS still allows to be issued, so it can’t be posted. Lower the extra in Adjust.`;

export const overDetail = (q: number, headroom: number, x: number): string =>
  `${qty(q)} to post · ${qty(headroom)} can still be issued · over by ${qty(x)}`;

export interface Stat {
  icon: 'list' | 'clipboard-list' | 'package';
  label: string;
  value: string;
  sub: string;
  tip: string;
}

/** About to post: the rows that will be sent (selected, quantity above 0). */
export function aboutToPost(rows: readonly { row: PostRow; q: number }[]): Stat[] {
  const n = rows.length;
  const mrs: string[] = [];
  const mrsLabels: string[] = [];
  for (const { row } of rows) {
    if (mrs.includes(row.mrsNumber)) continue;
    mrs.push(row.mrsNumber);
    mrsLabels.push(`${row.mrsNumber} (${row.requestorFullName || '—'})`);
  }
  const byUnit = new Map<string, number>();
  for (const { row, q } of rows) byUnit.set(row.unit || 'units', (byUnit.get(row.unit || 'units') ?? 0) + q);
  const unitParts = [...byUnit.entries()].map(([u, t]) => `${qty(t)} ${u}`);
  const total = rows.reduce((a, r) => a + r.q, 0);

  return [
    {
      icon: 'list',
      label: 'Item lines',
      value: String(n),
      sub: n === 1 ? '1 MIS line' : `${n} MIS lines`,
      tip: 'Each selected row becomes one line on the MIS.',
    },
    {
      icon: 'clipboard-list',
      label: 'MRS',
      value: String(mrs.length),
      sub: mrs.length ? `${mrs[0]}${mrs.length > 1 ? ` +${mrs.length - 1}` : ''}` : '—',
      tip: mrs.length
        ? `${mrs.length} ${plural(mrs.length, 'MRS line', 'MRS lines')} on this MIS: ${mrsLabels.join(' · ')}`
        : 'No MRS selected yet.',
    },
    {
      icon: 'package',
      label: 'Units',
      value: String(Math.round(total * 100) / 100),
      sub: byUnit.size === 1 ? unitParts[0] : byUnit.size > 1 ? `${byUnit.size} kinds of unit` : '—',
      tip: unitParts.length ? unitParts.join(' · ') : 'Nothing selected yet.',
    },
  ];
}

/** The serial caption under a serial row (slot 53). */
export function serialCaption(ticked: readonly string[]): string {
  if (ticked.length === 0) return 'No serials selected';
  return ticked.slice(0, 4).join(', ') + (ticked.length > 4 ? ` +${ticked.length - 4} more` : '');
}

/** The memo prefill: the MRS line's remarks, clamped to the cap (the full text stays in the popover). */
export const memoPrefill = (remarks: string): string => remarks.slice(0, MEMO_MAX_LENGTH);

/** The line filter (O-16): today's fields, read from the row's planned line. View only — never what posts. */
export function matchesLineQuery(row: PostRow, needle: string): boolean {
  return [row.itemCode, row.itemName, row.mrsNumber, row.requestorFullName, row.departmentName, row.endUseName]
    .some((f) => (f ?? '').toLowerCase().includes(needle));
}

/**
 * The batches still POSTING (not failed): the second-batch confirm is about these, and opens only when there is one
 * (the prototype's trigger). A picklist whose only open batches have failed goes straight to the dialog, where the
 * already-posting notice lists the lines those batches still hold.
 */
export function stillPosting(picklist: PicklistDto) {
  return (picklist.activePostingBatches ?? []).filter((b) => b.status === PostingStatusEnum.Posting);
}
